<template>
  <section class="reviews-band">
    <div class="mx-auto max-w-6xl px-4 py-12 sm:px-6 md:py-16">
      <div class="reviews-head">
        <h2>{{ title }}</h2>
        <p class="reviews-rating">{{ ratingLine }}</p>
      </div>

      <div class="reviews-grid">
        <figure v-for="review in featured" :key="review.reviewId" class="review-card">
          <div class="review-stars" :aria-label="`${review.stars} out of 5 stars`">
            <span v-for="n in 5" :key="n" :class="{ 'is-lit': n <= review.stars }">★</span>
          </div>
          <blockquote translate="no" lang="en">
            <strong>{{ review.title }}</strong>
            <p>{{ review.quote }}</p>
          </blockquote>
          <figcaption>
            <span translate="no">{{ review.author }}</span> ·
            <time translate="no" :datetime="review.date">{{ review.date }}</time> ·
            <a
              :href="review.sourceUrl"
              target="_blank"
              rel="noopener"
              data-conversion-event="mobile_app_proof_click"
              data-conversion-label="App Store review"
              :data-conversion-location="location"
            >{{ tr('App Store review') }}</a>
          </figcaption>
        </figure>
      </div>

      <p class="reviews-note">
        {{ tr('Quoted exactly as written, in the reviewer’s own language. This is a selection of reviews, not all of them. Reviews from our own team are left out of the selection, but the overall rating includes their ratings.') }}
      </p>

      <div class="reviews-more">
        <NuxtLink
          :to="localePath('/customers')"
          data-conversion-event="explore_click"
          data-conversion-label="Reviews customers link"
          :data-conversion-location="location"
        >{{ ctaLabel }}</NuxtLink>
        <a
          :href="APP_STORE_LISTING_URL"
          target="_blank"
          rel="noopener"
          data-conversion-event="mobile_app_proof_click"
          data-conversion-label="Reviews App Store listing"
          :data-conversion-location="location"
        >{{ tr('See all reviews on the App Store') }}</a>
      </div>
    </div>
  </section>
</template>

<script setup>
import {
  APP_STORE_LISTING_URL,
  appStoreRating,
  featuredAppStoreReviews as featured,
} from '~/data/customer-stories';

defineProps({
  eyebrow: { type: String, required: true },
  title: { type: String, required: true },
  ctaLabel: { type: String, required: true },
  location: { type: String, default: 'home-reviews' },
});

const { localePath } = useWorkspaceLocale();
const { tr } = useHomeI18n();

const ratingLine = computed(() =>
  tr('★ {score} from {count} ratings on the App Store (as of {date})')
    .replace('{score}', appStoreRating.average.toFixed(1))
    .replace('{count}', String(appStoreRating.count))
    .replace('{date}', appStoreRating.asOf),
);
</script>

<style scoped>
.reviews-band {
  border-top: 1px solid #e2e8f0;
  background: #ffffff;
}

.dark .reviews-band {
  border-color: #243247;
  background: #020617;
}

.reviews-head {
  max-width: 44rem;
}

.reviews-head h2 {
  margin-top: 0;
  color: #0f172a;
  font-size: clamp(1.9rem, 3.4vw, 2.75rem);
  font-weight: 900;
  letter-spacing: 0;
  line-height: 1.06;
}

.dark .reviews-head h2 {
  color: #f8fafc;
}

.reviews-grid {
  display: grid;
  gap: 1rem;
  margin-top: 2.25rem;
}

.review-card {
  display: flex;
  min-width: 0;
  flex-direction: column;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  background: #ffffff;
  box-shadow: 0 10px 30px rgb(15 23 42 / 0.05);
  padding: 1.25rem;
}

.dark .review-card {
  border-color: #243247;
  background: #0f172a;
}

.review-stars {
  display: flex;
  gap: 0.1rem;
  color: #cbd5e1;
  font-size: 0.95rem;
  letter-spacing: 0.05em;
}

.review-stars .is-lit {
  color: #f59e0b;
}

.review-card blockquote {
  margin-top: 0.85rem;
}

.review-card blockquote strong {
  color: #0f172a;
  font-size: 0.98rem;
  font-weight: 850;
  letter-spacing: 0;
}

.dark .review-card blockquote strong {
  color: #f8fafc;
}

.review-card blockquote p {
  margin-top: 0.5rem;
  color: #475569;
  font-size: 0.9rem;
  line-height: 1.65;
}

.dark .review-card blockquote p {
  color: #94a3b8;
}

.review-card figcaption {
  margin-top: auto;
  padding-top: 1rem;
  color: #64748b;
  font-size: 0.76rem;
  font-weight: 700;
}

.dark .review-card figcaption {
  color: #7c8ea5;
}

.reviews-rating {
  margin-top: 0.75rem;
  color: #475569;
  font-size: 0.95rem;
  font-weight: 700;
}

.dark .reviews-rating {
  color: #cbd5e1;
}

.reviews-note {
  margin-top: 1.25rem;
  max-width: 44rem;
  color: #64748b;
  font-size: 0.8rem;
  line-height: 1.6;
}

.dark .reviews-note {
  color: #94a3b8;
}

.review-card figcaption a {
  text-decoration: underline;
}

.reviews-more {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem 1.5rem;
  margin-top: 1.25rem;
}

.reviews-more a {
  /* 44px tap targets (they were 28px at 125% text); the negative margin keeps the row where it was. */
  display: inline-flex;
  align-items: center;
  min-height: 44px;
  margin-block: -0.5rem;
  /* Exactly half the added height on each side, at any text size. */
  margin-block: calc((1lh - 44px) / 2);
  color: #0e7490;
  font-size: 0.92rem;
  font-weight: 800;
}

.dark .reviews-more a {
  color: #67e8f9;
}

@media (min-width: 900px) {
  .reviews-grid {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
}
</style>
